import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, ArrowLeft, Home, Building2, MapPin, DollarSign } from 'lucide-react';
import { projects } from '../mock';

const ProjectComparison = () => {
  const navigate = useNavigate();
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showSelector, setShowSelector] = useState(true);

  const handleSelectProject = (project) => {
    if (selectedProjects.find(p => p.id === project.id)) {
      setSelectedProjects(selectedProjects.filter(p => p.id !== project.id));
    } else if (selectedProjects.length < 3) {
      setSelectedProjects([...selectedProjects, project]);
    }
  };

  const handleCompare = () => {
    if (selectedProjects.length >= 2) {
      setShowSelector(false);
    }
  };

  const handleReset = () => {
    setSelectedProjects([]);
    setShowSelector(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-700 hover:text-amber-700 transition-colors font-medium"
            >
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </button>
            {!showSelector && (
              <button
                onClick={handleReset}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              >
                Change Selection
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {showSelector ? (
          <>
            {/* Project Selector */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Compare <span className="bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">Projects</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Select 2-3 projects to compare their features, pricing, and amenities
              </p>
              <div className="flex items-center justify-center space-x-4">
                <div className="text-sm text-gray-600">
                  Selected: <span className="font-bold text-amber-600">{selectedProjects.length}/3</span>
                </div>
                {selectedProjects.length >= 2 && (
                  <button
                    onClick={handleCompare}
                    className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-8 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
                  >
                    Compare Selected Projects
                  </button>
                )}
              </div>
            </div>

            {/* Project Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => {
                const isSelected = selectedProjects.find(p => p.id === project.id);
                const canSelect = selectedProjects.length < 3 || isSelected;
                
                let borderClasses = 'border-gray-200';
                if (isSelected) {
                  borderClasses = 'border-amber-500 ring-4 ring-amber-100';
                } else if (canSelect) {
                  borderClasses = 'border-gray-200 hover:border-amber-300';
                } else {
                  borderClasses = 'border-gray-200 opacity-50 cursor-not-allowed';
                }

                return (
                  <div
                    key={project.id}
                    onClick={() => canSelect && handleSelectProject(project)}
                    className={`relative bg-white rounded-xl overflow-hidden shadow-lg border-2 transition-all duration-300 cursor-pointer ${borderClasses}`}
                  >
                    {isSelected && (
                      <div className="absolute top-4 right-4 z-10 bg-amber-500 text-white rounded-full p-2">
                        <Check size={20} />
                      </div>
                    )}
                    <img
                      src={project.image}
                      alt={project.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{project.name}</h3>
                      <div className="flex items-center text-gray-600 mb-3">
                        <MapPin size={16} className="mr-2 text-amber-600" />
                        <span className="text-sm">{project.location}</span>
                      </div>
                      <div className="text-lg font-bold text-amber-700">{project.price.split(' - ')[0]}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            {/* Comparison Table */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Project Comparison</h2>
              <p className="text-gray-600">Side-by-side comparison of selected properties</p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">Features</th>
                      {selectedProjects.map((project) => (
                        <th key={project.id} className="px-6 py-4 text-center font-semibold min-w-[250px]">
                          <div className="mb-2">{project.name}</div>
                          <div className="text-sm font-normal opacity-90">{project.location}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {/* Images */}
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Property Image</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4">
                          <img
                            src={project.image}
                            alt={project.name}
                            className="w-full h-40 object-cover rounded-lg"
                          />
                        </td>
                      ))}
                    </tr>

                    {/* Status */}
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Status</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center">
                          <span className="inline-block px-4 py-2 rounded-full bg-amber-100 text-amber-800 font-medium text-sm">
                            {project.status}
                          </span>
                        </td>
                      ))}
                    </tr>

                    {/* Price Range */}
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Price Range</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center font-bold text-amber-700 text-lg">
                          {project.price}
                        </td>
                      ))}
                    </tr>

                    {/* Configuration */}
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Configuration</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center text-gray-700">
                          {project.bedrooms}
                        </td>
                      ))}
                    </tr>

                    {/* Area */}
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Area</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center text-gray-700">
                          {project.area}
                        </td>
                      ))}
                    </tr>

                    {/* Timeline */}
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Timeline</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center text-gray-700 text-sm">
                          {project.timeline}
                        </td>
                      ))}
                    </tr>

                    {/* Floors */}
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Total Floors</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center text-gray-700">
                          {project.specifications.floors || 'N/A'}
                        </td>
                      ))}
                    </tr>

                    {/* Units */}
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900">Total Units</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center text-gray-700">
                          {project.specifications.units}
                        </td>
                      ))}
                    </tr>

                    {/* Parking */}
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Parking</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center text-gray-700">
                          {project.specifications.parking}
                        </td>
                      ))}
                    </tr>

                    {/* Amenities */}
                    <tr>
                      <td className="px-6 py-4 font-semibold text-gray-900 align-top">Amenities</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4">
                          <ul className="space-y-2">
                            {project.amenities.slice(0, 6).map((amenity) => (
                              <li key={amenity} className="flex items-center text-sm text-gray-700">
                                <Check size={16} className="text-green-600 mr-2 flex-shrink-0" />
                                <span>{amenity}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      ))}
                    </tr>

                    {/* Actions */}
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 font-semibold text-gray-900">Actions</td>
                      {selectedProjects.map((project) => (
                        <td key={project.id} className="px-6 py-4 text-center">
                          <button
                            onClick={() => navigate(`/project/${project.id}`)}
                            className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors w-full"
                          >
                            View Details
                          </button>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectComparison;
